import { useEffect, useRef } from 'react';

type RecaptchaApi = {
  ready(callback: () => void): void;
  render(
    container: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      'expired-callback': () => void;
      'error-callback': () => void;
    },
  ): number;
  reset(widgetId?: number): void;
};

declare global {
  interface Window {
    grecaptcha?: RecaptchaApi;
  }
}

let scriptPromise: Promise<RecaptchaApi> | null = null;

function loadRecaptcha(): Promise<RecaptchaApi> {
  if (window.grecaptcha) {
    return new Promise((resolve) => window.grecaptcha!.ready(() => resolve(window.grecaptcha!)));
  }
  if (scriptPromise) return scriptPromise;

  const pending = new Promise<RecaptchaApi>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (!window.grecaptcha) {
        reject(new Error('The reCAPTCHA script did not initialize.'));
        return;
      }
      window.grecaptcha.ready(() => resolve(window.grecaptcha!));
    };
    script.onerror = () => reject(new Error('The reCAPTCHA script could not be loaded.'));
    document.head.appendChild(script);
  }).catch((error: unknown) => {
    scriptPromise = null;
    throw error;
  });
  scriptPromise = pending;
  return pending;
}

export function RecaptchaWidget({
  siteKey,
  onToken,
  onError,
}: {
  siteKey: string;
  onToken: (token: string) => void;
  onError: (message: string) => void;
}) {
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let active = true;
    let widgetId: number | undefined;
    onToken('');
    onError('');

    loadRecaptcha()
      .then((recaptcha) => {
        if (!active || !container.current) return;
        widgetId = recaptcha.render(container.current, {
          sitekey: siteKey,
          callback: onToken,
          'expired-callback': () => onToken(''),
          'error-callback': () => {
            onToken('');
            onError('reCAPTCHA could not connect. Check your connection and try again.');
          },
        });
      })
      .catch(() => {
        if (active) onError('reCAPTCHA could not load. Check your connection and try again.');
      });

    return () => {
      active = false;
      if (widgetId !== undefined && window.grecaptcha) window.grecaptcha.reset(widgetId);
      container.current?.replaceChildren();
    };
  }, [siteKey, onToken, onError]);

  return <div className="recaptcha-widget" ref={container} aria-label="reCAPTCHA verification" />;
}
